import React, { useCallback, useEffect, useState } from 'react';

import useBlockBodyScroll from 'hooks/useBlockBodyScroll';
import { useQuery } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { orderBumpService } from 'services';
import { Divider, If } from 'shared';
import { IAlertDialog } from 'shared/AlertDialog';
import { Button, Switch, usePromiseCallback } from 'shared/Houston';
import { Spinner } from 'shared/Loader/styles';
import { orderBumpPreviewActions } from 'store/features';
import { selectInnerHeight } from 'store/selectors';
import { selectOrderBumpPreview } from 'store/selectors/orderBumpPreview';
import { enOrderBumpStatus, enOrderBumpStatusBase } from 'types/enums';
import { IOrderBumpPreview } from 'types/interfaces';

import Cancel from '@eduzz/houston-icons/Cancel';

import Info from './Info';
import {
  CloseButton,
  Drawer,
  Loading,
  RowSwitch,
  LabelSwitch,
  ErrorMessage,
  DrawerHeaderRow,
  DrawerHeaderColumn
} from './styles';

interface IProps {
  open: boolean;
  bumpId: number;
  onClose: () => void;
  alertComponent: React.RefObject<IAlertDialog>;
}

const Preview: React.FC<IProps> = ({ open = false, bumpId, onClose, alertComponent }) => {
  useBlockBodyScroll(open);

  const dispatch = useDispatch();

  const innerHeight = useSelector(selectInnerHeight);
  const { status, errorMsg, condition } = useSelector(selectOrderBumpPreview);
  const [checkedSwitch, setCheckedSwitch] = useState(false);
  const [disabledSwitch, setDisabledSwitch] = useState(false);

  const { isLoading } = useQuery(['orderBumpPreview', bumpId, open], async () => {
    const response = await orderBumpService.getOrderBumpById(Number(bumpId));
    dispatch(orderBumpPreviewActions.setState(response as IOrderBumpPreview));
  });

  useEffect(() => {
    setCheckedSwitch(status !== enOrderBumpStatus.ERROR && condition === enOrderBumpStatusBase.ACTIVE);
    setDisabledSwitch(status === enOrderBumpStatus.ERROR);
  }, [condition, status]);

  const [callbackUpdateStatus] = usePromiseCallback(
    () => orderBumpService.updateStatusOrderBump(bumpId, !checkedSwitch),
    [bumpId, checkedSwitch]
  );

  const navigate = useNavigate();

  const onCloseDrawer = useCallback(() => {
    dispatch(orderBumpPreviewActions.reset());
    onClose();
  }, [dispatch, onClose]);

  const confirmActiveOrInactiveBump = useCallback(() => {
    alertComponent.current?.showAlertDialog({
      title: `${condition !== enOrderBumpStatusBase.ACTIVE ? 'Ativar' : 'Desativar'} Order Bump`,
      text: `Você está ${condition !== enOrderBumpStatusBase.ACTIVE ? 'ativando' : 'desativando'} o Order Bump`,
      confirmActionTextButton: 'Confirmar',
      cancelActionTextButton: 'Cancelar',
      onConfirm: async () => {
        callbackUpdateStatus().then(() => {
          dispatch(orderBumpPreviewActions.setHasChange(true));
          setCheckedSwitch(checkedSwitch => !checkedSwitch);
        });
      }
    });
  }, [alertComponent, dispatch, condition, callbackUpdateStatus]);

  const redirectToUpdateOrderBump = useCallback(() => {
    navigate(`/orderbump/${bumpId}`, { replace: true });
  }, [navigate, bumpId]);

  return (
    <Drawer direction='right' size={505} open={open} onClose={onClose} height={innerHeight || '100vh'}>
      <DrawerHeaderRow alignItems='center' justifyContent='space-between' spacing='compact'>
        <DrawerHeaderColumn>
          <Button onClick={redirectToUpdateOrderBump}>Editar</Button>
        </DrawerHeaderColumn>
        <DrawerHeaderColumn>
          <CloseButton onClick={onCloseDrawer} startIcon={<Cancel />} variant='text'>
            Fechar
          </CloseButton>
        </DrawerHeaderColumn>
        <Divider margin='16px 0' />
      </DrawerHeaderRow>

      <If condition={isLoading}>
        <Loading alignItems='center' justifyContent='center'>
          <Spinner width='72' height='72' />
        </Loading>
      </If>

      <RowSwitch alignItems='center' justifyContent='flex-start' spacing='compact'>
        <Switch disabled={disabledSwitch} checked={checkedSwitch} onChange={confirmActiveOrInactiveBump} />
        <LabelSwitch className={condition}>{checkedSwitch ? 'Ativo' : 'Inativo'}</LabelSwitch>
        <If condition={Boolean(errorMsg)}>
          <ErrorMessage className={status}>
            {status === 'error' ? 'Erro' : 'Atenção'}: {errorMsg}
          </ErrorMessage>
        </If>
      </RowSwitch>
      <Divider margin='16px 0 24px 0' />
      <Info />
    </Drawer>
  );
};

export default React.memo(Preview);
